import axios from "@/utils/axios";
import {
  append,
  compose,
  defaultTo,
  either,
  find,
  findIndex,
  identity,
  includes,
  isEmpty,
  isNil,
  lensPath,
  lensProp,
  map,
  not,
  omit,
  over,
  path,
  pathOr,
  pluck,
  prop,
  propEq,
  propOr,
  propSatisfies,
  reject,
  set,
  sortBy,
  tap,
  uniq,
  toLower,
  descend,
  ascend,
  sortWith,
  sort,
  sum,
} from "ramda";
import {
  SET_ACTIVE_STUDENTS,
  SET_AVG_ELSA_SCORE,
  SET_DISTRIBUTION_EPS,
  SET_DISTRIBUTION_IELTS,
  SET_AVG_PRACTICE_TIME,
  SET_CURRENT_ORGANIZATION,
  SET_EPS,
  SET_LESSONS_TOTAL_TIME_PRACTICED,
  SET_LESSONS_TIME_PRACTICED,
  SET_STUDYSET_TIME_PRACTICED,
  SET_PENDING_INVITE_USERS,
  SET_WEEKLY_ACTIVE_STUDENTS,
  SOFT_RESET_ORGANIZATIONS,
  SET_INDIVIDUAL_LEADERBOARD,
  SET_TEAM_LEADERBOARD,
  SET_BRANCH_LEADERBOARD,
  ADD_TEAM_LEADERBOARD,
  ADD_INDIVIDUAL_LEADERBOARD,
  ADD_BRANCH_LEADERBOARD,
  LOADING_INDIVIDUAL_LEADERBOARD,
  LOADING_TEAM_LEADERBOARD,
  LOADING_BRANCH_LEADERBOARD,
  SET_PENTAGON,
  LESS_INDIVIDUAL_LEADERBOARD,
  HIDE_TEAM_LEADERBOARD,
  HIDE_BRANCH_LEADERBOARD,
  SET_AVERAGE_SCORE_SKILLS,
  LOADING_AVERAGE_SCORE_SKILLS,
  SET_GENERAL_OVERVIEW,
  SET_SUM_ACTIVE_STUDENTS,
} from "../constants";
import { ADMIN, ASSOCIATE_ADMIN, STUDENT, TEACHER, levelUserRoles } from "@/constants/userRoles";
import chartsFragment, {
  getAbstractAvgPracticeTime,
  getAbstractGetChartData,
  getAbstractLessonsPracticed,
  getAbstractStudySetsPracticed,
  getAbstractGetChartDataWithoutEmptyGuard,
} from "./chartsFragment";
import { SET_OVERVIEW_DATA, SET_PREV_OVERVIEW_DATA, RESET_OVERVIEW_DATA } from "../constants/students";
import { extractDate } from "@/utils/dateInterval";
import { parseISO } from "date-fns";
import subDays from "date-fns/subDays";
import differenceInDays from "date-fns/differenceInDays";
import formatISO9075 from "date-fns/formatISO9075";
import { ELSA_AI, ELSA_SPEAK, SPEECH_ANALYZER } from "@/constants/generic";
import {
  SELECT_ORGANIZATION_TYPE,
  SELECT_ORGANIZATION_TYPE_FOR_ADMIN,
  K12,
  COMPANY,
} from "@/constants/organizationType";

const ADD_ORGANIZATION_TYPES = "add_organization_types";
const UPDATE_CURRENT_ORGANIZATION_USERS = "update_current_organization_users";
const UPDATE_ORGANIZATION_USER = "update_organization_user";
const ADD_USER_ORGANIZATION = "add_user_organization";
const UPDATE_USER_ORGANIZATIONS = "update_user_organizations";
const UPDATE_USER_ALL_ORGANIZATIONS = "update_user_all_organizations";
const SET_CURRENT_ORGANIZATION_ID = "set_current_organization_id";
const SET_CURRENT_ORGANIZATION_ADMINS = "set_current_organization_admins";
const WITHDRAW_ORGANIZATION_ADMINS = "withdraw_organization_admins";
const RESET_USER_ORGANIZATION = "reset_user_organization";
const ADD_USER_ORGANIZATIONS = "add_user_organizations";
const RESET_ORGANIZATIONS = "reset_organizations";

const { state: chartFragmentState, mutations: chartFragmentMutations, actions: chartFragmentActions } = chartsFragment;

const parseRoles = over(lensProp("roles"), compose(sortBy(identity), uniq));
const getLevelRole = role => {
  return find(propEq("value", role), levelUserRoles)["level"];
};

// initial state
const setInitialState = () => ({
  // Static types used to fill select/drop down boxes
  types: [],
  status: [],

  // All the organizations the user belongs to
  userOrganizations: [],
  userAllOrganizations: [],

  // Data of the user organizations relationship
  userOrganizationsData: [],

  // Data of the current organization
  currentOrganizationId: null,
  currentOrganization: {},

  // Users of the current organization to display on users table
  currentOrganizationUsers: {
    next: null,
    previous: null,
    results: [],
  },

  // Admin users of this organization
  currentOrganizationAdmins: [],

  withdrawOrganizationAdmins: [],

  pendingInviteUsers: {},

  generalOverviewData: {},

  // Attributes for chart data
  ...chartFragmentState,
  loadingIndividualLeaderboard: false,
  loadingTeamLeaderboard: false,
  loadingBranchLeaderboard: false,
  loadingAverageScoreSkills: false,
});

const state = setInitialState();

// getters
const getters = {
  userOrganizations: propOr([], "userOrganizations"),
  userAllOrganizations: propOr([], "userAllOrganizations"),
  currentOrganization: propOr({}, "currentOrganization"),
  hasOrganizations: compose(not, isEmpty, propOr([], "userOrganizationsData")),
  currentOrganizationId: prop("currentOrganizationId"),
  currentOrganizationType: path(["currentOrganization", "type"]),
  currentOrganizationCode: path(["currentOrganization", "code"]),
  organizationHasSpecialAssessmentTest: path(["currentOrganization", "has_custom_assessment_test"]),
  isOrganizationUseDummyEmail: path(["currentOrganization", "use_dummy_email"]),
  currentOrganizationUsers: propOr([], "currentOrganizationUsers"),
  currentOrganizationAdmins: propOr([], "currentOrganizationAdmins"),
  userOrganizationsData: propOr([], "userOrganizationsData"),
  currentOrgPayment: pathOr({}, ["currentOrganization", "payment"]),
  curOrgPurchasedProducts: pathOr([], ["currentOrganization", "purchased_products"]),
  currentOrganizationFeaturesAvailable: pathOr([], ["currentOrganization", "dashboard_features_avaiable"]),
  currentOrgType: state => state.currentOrganization.type,
  userOrganizationData: state => organizationId => {
    const organization = find(propEq("organizationId", organizationId), state.userOrganizationsData);
    return defaultTo({}, organization);
  },
  isNovaOrg: path(["currentOrganization", "is_nova"]),
  withdrawOrganizationAdmins: propOr([], "withdrawOrganizationAdmins"),
  currentOrganizationUser: (state, getters) => userId => {
    const users = getters.currentOrganizationUsers;
    return compose(defaultTo({}), find(propEq("id", userId)), pathOr([], ["results"]))(users);
  },
  userHasOrganizationRole: (state, getters) => (role, organizationId = getters.currentOrganizationId) => {
    const userOrganizationsData = getters.userOrganizationsData;
    const hasRole = compose(
      propSatisfies(includes(role), "roles"),
      defaultTo({ roles: [] }),
      find(propEq("organizationId", organizationId))
    )(userOrganizationsData);
    return !!hasRole;
  },
  organizationOnlyAdmin: (state, getters) => (organizationId, userId) => {
    const organizationAdmins = getters.currentOrganizationAdmins;
    return compose(isEmpty, reject(propEq("id", userId)), defaultTo([]))(organizationAdmins);
  },
  isOrganizationAdmin: (state, getters, rootState, rootGetters) => {
    const currentOrganizationId = getters.currentOrganizationId;
    return (
      getters.userHasOrganizationRole(ADMIN, currentOrganizationId) ||
      getters.isGeneralAdmin ||
      rootGetters["user/isElsaAdmin"]
    );
  },
  isOrganizationAssociateAdmin: (state, getters) => {
    const currentOrganizationId = getters.currentOrganizationId;
    return getters.userHasOrganizationRole(ASSOCIATE_ADMIN, currentOrganizationId);
  },
  isOrganizationTeacher: (state, getters) => {
    const currentOrganizationId = getters.currentOrganizationId;
    return getters.userHasOrganizationRole(TEACHER, currentOrganizationId);
  },
  isOrganizationStudent: (state, getters) => {
    const currentOrganizationId = getters.currentOrganizationId;
    return getters.userHasOrganizationRole(STUDENT, currentOrganizationId);
  },
  userIsOnlyStudent: (state, getters) => {
    return (
      !getters.isOrganizationTeacher &&
      !getters.isOrganizationAdmin &&
      !getters.isOrganizationAssociateAdmin &&
      getters.isOrganizationStudent
    );
  },
  isOrganizationTeacherOnly: (state, getters) => {
    return (
      getters.isOrganizationTeacher &&
      !getters.isOrganizationAdmin &&
      !getters.isOrganizationAssociateAdmin
    );
  },
  isGeneralAdmin: (state, getters) => {
    let generalOrganizationData = null;
    const currentOrganization = getters.currentOrganization;
    if (currentOrganization.parent) {
      const parent_id = currentOrganization.parent;
      generalOrganizationData = state.userOrganizationsData.find(org => org.organizationId === parent_id);
    } else if (currentOrganization.child && currentOrganization.child.length > 0) {
      const currentOrganizationId = getters.currentOrganizationId;
      generalOrganizationData = state.userOrganizationsData.find(org => org.organizationId === currentOrganizationId);
    }
    return generalOrganizationData ? generalOrganizationData["is_general_admin"] : false;
  },
  hasChildOrg: (state, getters) => {
    const currentOrganization = getters.currentOrganization;
    return currentOrganization.child && currentOrganization.child.length > 0;
  },
  hasParentOrg: (state, getters) => {
    const currentOrganization = getters.currentOrganization;
    return !!currentOrganization.parent;
  },
  organizationsFilter: (state, getters) => {
    const data = [];

    getters.userAllOrganizations.forEach(org => {
      const isAdmin = getters.userHasOrganizationRole(ADMIN, org.id);
      const orgCopy = { ...org, branch_type: "parent" };
      data.push(orgCopy);

      if (!org.parent && isAdmin && org.child) {
        data.push(...org.child.map(child => ({ ...child, branch_type: "child" })));
      }
    });
    // Rechecking and removing "parent" branch_type if both "child" and "parent" exists
    const orgIdToBranchTypeMap = {};
    const finalData = data.filter(org => {
      if (orgIdToBranchTypeMap[org.id] === "child" && org.branch_type === "parent") {
        return false;
      }

      if (org.branch_type === "parent" || org.branch_type === "child") {
        orgIdToBranchTypeMap[org.id] = org.branch_type;
      }

      return true;
    });
    // Rechecking and switch "parent" to "child" if parent id exists
    const outputJson = finalData.map(item => {
      if (item.branch_type === "parent" && item.parent !== null && orgIdToBranchTypeMap[item.parent] === "parent") {
        return { ...item, branch_type: "child" };
      } else {
        return item;
      }
    });

    return outputJson;
  },
  hasFilter: (state, getters) => {
    return getters.organizationsFilter.length > 1;
  },
  pendingUsersCount: pathOr(0, ["pendingInviteUsers", "count"]),
  pendingInviteUsersPage: pathOr([], ["pendingInviteUsers", "results"]),
  generalOverviewData: propOr({}, "generalOverviewData"),
  isElsaSpeakProduct: (state, getters) => {
    const hasPurchasedProducts = includes(ELSA_SPEAK)(getters.curOrgPurchasedProducts);
    return !!hasPurchasedProducts;
  },
  SAonly: (state, getters) => {
    const SAonly =
      getters.curOrgPurchasedProducts.length == 1 && includes(SPEECH_ANALYZER)(getters.curOrgPurchasedProducts);
    return !!SAonly;
  },
  isOrganizationK12: (state, getters) => {
    return getters.currentOrganization.type === K12
  },
  isOrganizationCompany: (state, getters) => {
    return getters.currentOrganization.type === COMPANY
  },
  isAdminOfAnOrgHasBranches: (_, { isGeneralAdmin, hasChildOrg }, rootState, rootGetters) => {
    return (isGeneralAdmin || rootGetters["user/isElsaAdmin"]) && hasChildOrg
  },
  enableElsaAI: (state, getters) => {
    if (getters.isOrganizationCompany) return true;
    else return includes(ELSA_AI)(getters.currentOrganizationFeaturesAvailable);
  }
};

// actions
const actions = {
  async loadUserOrganizationsData({ dispatch }) {
    await dispatch("getUserOrganizations");
    await dispatch("getUserAllOrganizations");
    await dispatch("getUserOrganizationsData");
  },
  async reLoadUserOrganizationsData({ dispatch }) {
    await dispatch("getUserOrganizations");
    await dispatch("getUserAllOrganizations");
    await dispatch("getUserOrganizationsData");
  },
  async getUserOrganizations({ commit, rootGetters }) {
    if (rootGetters["user/isElsaAdmin"]) {
      return;
    }

    return axios.get("/v1/organizations/").then(response => commit(UPDATE_USER_ORGANIZATIONS, response.data.results));
  },
  async getUserAllOrganizations({ commit, rootGetters }) {
    if (rootGetters["user/isElsaAdmin"]) {
      return;
    }
    const params = { no_pagination: true };

    return axios
      .get("/v1/organizations/", { params })
      .then(response => commit(UPDATE_USER_ALL_ORGANIZATIONS, response.data));
  },
  getCurrentOrganizationUsers({ dispatch }, { roles = null, search = null } = {}) {
    const params = reject(either(isNil, isEmpty))({ roles, search });

    return dispatch("getCurrentOrganizationUsersPage", params);
  },
  setCurrentOrganization({ commit }, { organizationId } = {}) {
    commit(SET_CURRENT_ORGANIZATION_ID, parseInt(organizationId));
    return axios
      .get(`v1/organizations/${organizationId}/`)
      .then(response => commit(SET_CURRENT_ORGANIZATION, response.data));
  },
  setCurrentOrganizationData({ commit }, data = {}) {
    commit(SET_CURRENT_ORGANIZATION, data)
  },
  getUserOrganizationsData({ commit, getters, rootGetters }) {
    if (rootGetters["user/isElsaAdmin"]) {
      return;
    }

    const userId = rootGetters["user/getUserId"];
    const userOrganizationsIds = pluck("id", getters.userAllOrganizations);
    const getUserOrganizationsData = map(organizationId => {
      return axios.get(`v1/organizations/${organizationId}/users/${userId}`).then(response => ({
        ...response.data,
        roles: sort(descend(getLevelRole), response.data["roles"]),
        organizationId,
      }));
    })(userOrganizationsIds);

    return Promise.all(getUserOrganizationsData).then(userOrganizationData =>
      commit(ADD_USER_ORGANIZATIONS, userOrganizationData)
    );
  },
  getUserOrganizationData({ commit }, { organizationId, userId } = {}) {
    return axios.get(`v1/organizations/${organizationId}/users/${userId}`).then(response =>
      commit(ADD_USER_ORGANIZATION, {
        userOrganizationData: response.data,
        organizationId,
      })
    );
  },
  getCurrentOrganization({ commit, state }) {
    if (Object.keys(state.currentOrganization).length === 0) {
      const organizationId = state.currentOrganizationId;
      return axios
        .get(`/v1/organizations/${organizationId}/`)
        .then(response => commit(SET_CURRENT_ORGANIZATION, response.data));
    }
  },
  setCurrentOrganizationId({ commit }, { organizationId } = {}) {
    commit(SET_CURRENT_ORGANIZATION_ID, organizationId);
  },
  getOrganizationTypes({ commit, rootGetters }) {
    const types = SELECT_ORGANIZATION_TYPE_FOR_ADMIN;
    commit(ADD_ORGANIZATION_TYPES, types);
    // return axios.get('/v1/organizations/types/').then((response) => commit(ADD_ORGANIZATION_TYPES, response.data.results));
  },
  getCurrentOrganizationAdmins({ commit, state }) {
    const organizationId = state.currentOrganizationId || state.currentOrganization.id;
    const options = {
      params: {
        roles: [ADMIN, ASSOCIATE_ADMIN].join(),
      },
    };

    return axios.get(`/v1/organizations/${organizationId}/users`, options).then(response => {
      commit(SET_CURRENT_ORGANIZATION_ADMINS, {
        currentOrganizationAdmins: response.data.results,
      });
    });
  },
  setOrganizationAdmins({ commit }, { organizationId = null } = {}) {
    const options = {
      params: {
        roles: [ADMIN, ASSOCIATE_ADMIN].join(),
      },
    };

    return axios
      .get(`/v1/organizations/${organizationId}/users`, options)
      .then(response => {
        commit(SET_CURRENT_ORGANIZATION_ADMINS, {
          currentOrganizationAdmins: response.data.results,
        });
      })
      .then(() => commit(SET_CURRENT_ORGANIZATION_ID, parseInt(organizationId)));
  },
  getWithdrawOrganizationAdmins({ commit }, { organizationId }) {
    const options = {
      params: {
        roles: ADMIN,
      },
    };

    return axios
      .get(`/v1/organizations/${organizationId}/users`, options)
      .then(response => commit(WITHDRAW_ORGANIZATION_ADMINS, response.data.results));
  },
  getOrganizationUser(_, { organizationId, userId }) {
    return axios.get(`v1/organizations/${organizationId}/users/${userId}`).then(propOr({}, "data"));
  },
  _getCurrentOrganizationUsersPage(
    { getters },
    { page = 1, roles = null, organizationId: _organizationId = null, search = null } = {}
  ) {
    const organizationId = _organizationId || getters.currentOrganizationId;
    const params = {
      page,
      roles,
      search,
    };

    return axios.get(`v1/organizations/${organizationId}/users`, { params });
  },
  getCurrentOrganizationUsersPage({ commit, getters, dispatch }, { page = 1, roles = null, search = null }) {
    const organizationId = getters.currentOrganization.id;
    const params = {
      page,
      roles,
      organizationId,
      search,
    };

    return dispatch("_getCurrentOrganizationUsersPage", params).then(response =>
      commit(UPDATE_CURRENT_ORGANIZATION_USERS, response.data)
    );
  },
  postOrganizationUser({ commit }, { organizationId, userId, roles }) {
    return axios
      .post(`v1/organizations/${organizationId}/users/${userId}`, { roles })
      .then(
        tap(response => {
          const data = response.data;
          commit(UPDATE_ORGANIZATION_USER, { userData: data, organizationId });
        })
      )
      .then(propOr({}, "data"));
  },
  updateOrganizationUserInfo({ commit }, { organizationId, userId, first_name, last_name, roles }) {
    return axios
      .patch(`v1/organizations/${organizationId}/users/${userId}/`, {
        first_name,
        last_name,
        roles,
      })
      .then(
        tap(response => {
          const data = response.data;
          commit(UPDATE_ORGANIZATION_USER, { userData: data, organizationId });
        })
      );
  },
  removeFromOrganization(_, { userId, organizationId, isRemoveUserFromSystem = false }) {
    if (isRemoveUserFromSystem)
      return axios.delete(`/v1/organizations/${organizationId}/users/${userId}/`, {
        params: { is_remove_completely: 1 },
      });
    else return axios.delete(`/v1/organizations/${organizationId}/users/${userId}/`);
  },
  addRoleToUserInOrganization(_, { userId, organizationId, roles = [] }) {
    return axios.post(`/v1/organizations/${organizationId}/users/${userId}/`, {
      roles,
    });
  },
  getGeneralOverview({ commit, state }) {
    const organizationId = state.currentOrganizationId;

    return axios
      .get(`/v1/organizations/${organizationId}/general-overview`)
      .then(response => commit(SET_GENERAL_OVERVIEW, response.data));
  },
  reset({ commit }) {
    return commit(RESET_ORGANIZATIONS);
  },
  softReset({ commit }) {
    return commit(SOFT_RESET_ORGANIZATIONS);
  },
  async getChartDataNoPeriod({ dispatch, getters }, { organizationId: _organizationId = null } = {}) {
    const organizationId = _organizationId || getters.currentOrganizationId;

    const options = {
      organizationId,
    };
    await Promise.all([dispatch("getDistributionEps", options), dispatch("getAvgElsaScore", options)]);
    await Promise.all([dispatch("getPentagon", options), dispatch("getDistributionIelts", options)]);
  },
  async getChartDataPeriod(
    { dispatch, getters },
    { to = Date.now(), from = Date.now(), scope, organizationId: _organizationId = null } = {}
  ) {
    const organizationId = _organizationId || getters.currentOrganizationId;

    const options = {
      organizationId,
      from: extractDate(from),
      to: extractDate(to),
      scope,
    };
    await Promise.all([
      // dispatch("getOverview", options),
      dispatch("getLessonsTotalTimePracticed", options),
      dispatch("getAvgPracticeTime", options),
      dispatch("getWeeklyActiveStudents", options),
      dispatch("getSumActiveUsers", options),
    ]);
    // await Promise.all([
    // 	// dispatch('getAvgPracticeLessons', options),
    // 	// dispatch('getPracticeTime', options),
    // ]);
    await Promise.all([dispatch("getActiveStudents", options), dispatch("getEPS", options)]);
    Promise.all([
      dispatch("getPrevOverview", options),
      // dispatch('getAssignmentLessons', options),
      // dispatch('getAssignmentStudysets', options),
      dispatch("getAverageScoreSkills", options),
    ]);
  },
  getLessonsTotalTimePracticed({ commit }, { organizationId, from, to, scope } = {}) {
    const url = `/v1/reports/organizations/${organizationId}/lessons_practiced`;
    return getAbstractLessonsPracticed({
      axios,
      commit,
      mutationId: SET_LESSONS_TOTAL_TIME_PRACTICED,
      url,
      from,
      to,
      scope,
    });
  },
  getAssignmentLessons({ commit }, { organizationId, from, to, scope } = {}) {
    const url = `/v1/reports/organizations/${organizationId}/assignment_lessons`;
    return getAbstractLessonsPracticed({
      axios,
      commit,
      mutationId: SET_LESSONS_TIME_PRACTICED,
      url,
      from,
      to,
      scope,
    });
  },
  getAssignmentStudysets({ commit }, { organizationId, from, to, scope } = {}) {
    const url = `/v1/reports/organizations/${organizationId}/assignment_studysets`;
    return getAbstractStudySetsPracticed({
      axios,
      commit,
      mutationId: SET_STUDYSET_TIME_PRACTICED,
      url,
      from,
      to,
      scope,
    });
  },
  getAvgPracticeTime({ commit }, { organizationId, from, to, scope } = {}) {
    const url = `v1/reports/organizations/${organizationId}/avg_practice_time`;

    return getAbstractAvgPracticeTime({
      axios,
      commit,
      url,
      mutationId: SET_AVG_PRACTICE_TIME,
      from,
      to,
      name: "avg_practice_time",
      scope,
    });
  },
  getActiveStudents({ commit }, { organizationId, from, to, scope } = {}) {
    const url = `v1/reports/organizations/${organizationId}/active_students`;

    return getAbstractGetChartData({
      axios,
      commit,
      url,
      mutationId: SET_ACTIVE_STUDENTS,
      from,
      to,
      name: "active_students",
      scope,
    });
  },
  getEPS({ commit }, { organizationId, from, to, scope } = {}) {
    const url = `v1/reports/organizations/${organizationId}/eps`;

    return getAbstractGetChartData({
      axios,
      commit,
      url,
      mutationId: SET_EPS,
      from,
      to,
      name: "eps",
      scope,
    });
  },
  getPentagon({ commit }, { organizationId } = {}) {
    const url = `v1/reports/organizations/${organizationId}/pentagon`;
    return getAbstractGetChartData({
      axios,
      commit,
      url,
      mutationId: SET_PENTAGON,
      name: "pentagon",
    });
  },
  getOverview({ commit }, { organizationId, from, to, scope } = {}) {
    const url = `v1/reports/organizations/${organizationId}/overview`;

    return axios.get(url, { params: { from, to, scope } }).then(response => commit(SET_OVERVIEW_DATA, response.data));
  },
  getSumActiveUsers({ commit }, { organizationId, from, to, scope } = {}) {
    const url = `v1/reports/organizations/${organizationId}/sum_active_users`;
    return axios.get(url, { params: { from, to, scope } }).then(response => commit(SET_SUM_ACTIVE_STUDENTS, response.data));
  },
  getPrevOverview({ commit }, { organizationId, from, to, scope } = {}) {
    const url = `v1/reports/organizations/${organizationId}/lessons_practiced`;
    let tempFrom = parseISO(from);
    let tempTo = parseISO(to);
    let previousTo = subDays(tempFrom, 1);
    let previousFrom = subDays(previousTo, differenceInDays(tempTo, tempFrom));

    return getAbstractLessonsPracticed({
      axios,
      commit,
      mutationId: SET_PREV_OVERVIEW_DATA,
      url,
      from: formatISO9075(previousFrom, { representation: "date" }),
      to: formatISO9075(previousTo, { representation: "date" }),
      scope,
    });
  },
  getWeeklyActiveStudents({ commit }, { organizationId, from, to } = {}) {
    // const url = `v1/reports/organizations/${organizationId}/active_students`
    const url = `v1/reports/organizations/${organizationId}/weekly_active_students`;

    return getAbstractGetChartData({
      axios,
      commit,
      url,
      mutationId: SET_WEEKLY_ACTIVE_STUDENTS,
      from,
      to,
      name: "weekly_active_students",
      scope: "weekly",
    });
  },
  exportReport({ commit, getters }, { organizationId: _organizationId, email, from, to }) {
    const organizationId = _organizationId || getters.currentOrganizationId;
    const params = {
      from: extractDate(from),
      to: extractDate(to),
      email,
    };

    return axios.get(`v1/reports/organizations/${organizationId}/export_report`, { params });
  },
  getPendingInviteUsers({ commit, getters }, { roles, page = 1, organizationId: _organizationId }) {
    const organizationId = _organizationId || getters.currentOrganizationId;

    const url = `v1/invitations/organization/${organizationId}/`;

    return axios
      .get(url, {
        params: {
          roles,
          page,
          limit: 10,
        },
      })
      .then(response => commit(SET_PENDING_INVITE_USERS, response.data));
  },
  getPendingInviteAdmins({ dispatch }, options = {}) {
    return dispatch("getPendingInviteUsers", {
      ...options,
      roles: ADMIN,
    });
  },
  getPendingInviteTeachers({ dispatch }, options = {}) {
    return dispatch("getPendingInviteUsers", {
      ...options,
      roles: TEACHER,
    });
  },
  getPendingInviteStudents({ dispatch }, options = {}) {
    return dispatch("getPendingInviteUsers", {
      ...options,
      roles: STUDENT,
    });
  },
  removeInvite({ commit, getters }, inviteId) {
    const url = `v1/invitations/${inviteId}/`;
    return axios.delete(url).then(rep => {
      const count = getters.pendingUsersCount - 1;
      // should be update backend to consisten number pending invitation (include teacher or not)
      const pendingInviteUsers = reject(propEq("id", inviteId))(getters.pendingInviteUsersPage);
      commit(SET_PENDING_INVITE_USERS, {
        count: count,
        results: pendingInviteUsers,
      });
    });
  },
  getLogoUploadUrl(_, { fileSize, contentType, organizationId }) {
    const params = {
      content_type: contentType,
      file_size: fileSize,
    };
    return axios.get(`/v1/organizations/${organizationId}/upload`, { params });
  },
  getAvgElsaScore({ commit }, { organizationId } = {}) {
    const url = `v1/reports/organizations/${organizationId}/avg_elsa_score`;

    return getAbstractGetChartData({
      axios,
      commit,
      url,
      mutationId: SET_AVG_ELSA_SCORE,
      name: "avg_elsa_score",
    });
  },
  getDistributionEps({ commit }, { organizationId, scope } = {}) {
    const url = `v1/reports/organizations/${organizationId}/distribution_eps`;

    return getAbstractGetChartData({
      axios,
      commit,
      url,
      mutationId: SET_DISTRIBUTION_EPS,
      name: "distribution_eps",
      scope,
    });
  },
  getDistributionIelts({ commit }, { organizationId } = {}) {
    const url = `v1/reports/organizations/${organizationId}/distribution_ielts`;

    return getAbstractGetChartData({
      axios,
      commit,
      url,
      mutationId: SET_DISTRIBUTION_IELTS,
      name: "distribution_ielts",
    });
  },
  async getIndividualLeaderboard(
    { commit, state, getters },
    {
      organizationId: _organizationId = null,
      from: _from,
      to: _to,
      individual_sort_by,
      individual_order_by,
      individual_cursor = 0,
      page_size = 5,
      replace = false,
    } = {}
  ) {
    const organizationId = _organizationId || state.currentOrganizationId || state.currentOrganization.id;

    if (!organizationId) {
      return;
    }
    const from = extractDate(_from);
    const to = extractDate(_to);

    const url = `v1/reports/organizations/${organizationId}/leaderboard_individual`;
    const url_admin = `v1/reports/organizations/${organizationId}/parent_leaderboard_individual`;
    commit(LOADING_INDIVIDUAL_LEADERBOARD, true);
    try {
      await getAbstractGetChartDataWithoutEmptyGuard({
        axios,
        commit,
        url: getters.isGeneralAdmin && getters.hasChildOrg ? url_admin : url,
        mutationId: replace ? SET_INDIVIDUAL_LEADERBOARD : ADD_INDIVIDUAL_LEADERBOARD,
        name: "individual_leaderboard",
        from,
        to,
        sort_by: individual_sort_by,
        order_by: individual_order_by,
        page: individual_cursor,
        page_size: page_size,
      });
    } finally {
      commit(LOADING_INDIVIDUAL_LEADERBOARD, false);
    }
  },
  async getTeamLeaderboard(
    { commit, state, getters },
    {
      organizationId: _organizationId,
      from: _from,
      to: _to,
      team_sort_by,
      team_order_by,
      team_cursor = 0,
      replace = false,
    } = {}
  ) {
    const organizationId = _organizationId || state.currentOrganizationId || state.currentOrganization.id;
    const from = extractDate(_from);
    const to = extractDate(_to);

    const url = `v1/reports/organizations/${organizationId}/leaderboard_team`;
    const url_admin = `v1/reports/organizations/${organizationId}/parent_leaderboard_team`;
    commit(LOADING_TEAM_LEADERBOARD, true);
    try {
      await getAbstractGetChartDataWithoutEmptyGuard({
        axios,
        commit,
        url: getters.isGeneralAdmin && getters.hasChildOrg ? url_admin : url,
        mutationId: replace ? SET_TEAM_LEADERBOARD : ADD_TEAM_LEADERBOARD,
        name: "team_leaderboard",
        from,
        to,
        sort_by: team_sort_by,
        order_by: team_order_by,
        page: team_cursor,
      });
    } finally {
      commit(LOADING_TEAM_LEADERBOARD, false);
    }
  },
  async getBranchLeaderboard(
    { commit, state },
    {
      organizationId: _organizationId,
      from: _from,
      to: _to,
      branch_sort_by,
      branch_order_by,
      branch_cursor = 0,
      replace = false,
    } = {}
  ) {
    const organizationId = _organizationId || state.currentOrganizationId || state.currentOrganization.id;
    const from = extractDate(_from);
    const to = extractDate(_to);

    const url = `v1/reports/organizations/${organizationId}/leaderboard_branch`;

    commit(LOADING_BRANCH_LEADERBOARD, true);
    try {
      await getAbstractGetChartDataWithoutEmptyGuard({
        axios,
        commit,
        url,
        mutationId: replace ? SET_BRANCH_LEADERBOARD : ADD_BRANCH_LEADERBOARD,
        name: "branch_leaderboard",
        from,
        to,
        sort_by: branch_sort_by,
        order_by: branch_order_by,
        page: branch_cursor,
      });
    } finally {
      commit(LOADING_BRANCH_LEADERBOARD, false);
    }
  },
  lessIndividualLeaderboard({ commit }, { page: page, page_size: page_size } = {}) {
    commit(LESS_INDIVIDUAL_LEADERBOARD, { page: page, page_size: page_size });
  },
  hideTeamLeaderboard({ commit }) {
    commit(HIDE_TEAM_LEADERBOARD);
  },
  hideBranchLeaderboard({ commit }) {
    commit(HIDE_BRANCH_LEADERBOARD);
  },
  ...chartFragmentActions,
  async getAverageScoreSkills({ commit, state, rootGetters }, { organizationId, from, to } = {}) {
    const url = `v1/reports/organizations/${organizationId}/skill_score`;
    commit(LOADING_AVERAGE_SCORE_SKILLS, true);
    try {
      await getAbstractGetChartDataWithoutEmptyGuard({
        axios,
        commit,
        url,
        mutationId: SET_AVERAGE_SCORE_SKILLS,
        name: "lowest_average_score_skills",
        from,
        to,
      });
    } finally {
      commit(LOADING_AVERAGE_SCORE_SKILLS, false);
    }
  },
  resetOverview({ commit }) {
    commit(RESET_OVERVIEW_DATA);
  },
  changePassDummyEmail(_, { organizationId, userId, new_password, confirm_password }) {
    const payload_change_pass = {
      new_password,
      confirm_password,
    };
    return axios.post(`/v1/organizations/${organizationId}/users/${userId}/change-pass-dummy-email`, payload_change_pass);
  },
};

// mutations
const mutations = {
  [ADD_ORGANIZATION_TYPES](state, organizationTypes) {
    /*state.types = reject(propEq('id', 'school'))(organizationTypes).map((type) => {
      const typeId = type.id;
      return {
        label: typeId.charAt(0).toUpperCase() + typeId.slice(1),
        value: typeId
      };
    });*/
    state.types = organizationTypes;
  },
  [UPDATE_ORGANIZATION_USER](state, { userData }) {
    const userIndex = compose(
      defaultTo(0),
      findIndex(propEq("id", userData.id)),
      pathOr([], ["currentOrganizationUsers", "results"])
    )(state);
    const newUsers = set(lensPath(["currentOrganizationUsers", "results", userIndex]), userData, state);

    state.currentOrganizationUsers = newUsers.currentOrganizationUsers;
  },
  [UPDATE_USER_ORGANIZATIONS](state, userOrganizations) {
    state.userOrganizations = [...userOrganizations];
  },
  [UPDATE_USER_ALL_ORGANIZATIONS](state, organizations) {
    state.userAllOrganizations = [...organizations];
  },
  [SET_CURRENT_ORGANIZATION](state, currentOrganization) {
    state.currentOrganization = { ...currentOrganization };
  },
  [UPDATE_CURRENT_ORGANIZATION_USERS](state, organizationUsers) {
    const parsedOrganizationUsers = over(lensProp("results"), map(parseRoles))(organizationUsers);

    state.currentOrganizationUsers = { ...parsedOrganizationUsers };
  },
  [RESET_USER_ORGANIZATION](state) {
    state.userOrganizationsData = [];
  },
  [ADD_USER_ORGANIZATION](state, { userOrganizationData, organizationId }) {
    const userOrganizations = reject(propEq("organizationId", organizationId))(state.userOrganizationsData);
    const parsedOrganizationData = parseRoles(userOrganizationData);
    state.userOrganizationsData = append({ ...parsedOrganizationData, organizationId }, userOrganizations);
  },
  [ADD_USER_ORGANIZATIONS](state, userOrganizationData) {
    // const parsedUserOrganizationData = map(parseRoles)(userOrganizationData);
    const sortedUserOrganizationData = sortWith([
      descend(compose(getLevelRole, toLower, prop(0), propOr(["student"], "roles"))),
      descend(prop("organization_user_id")),
    ])(userOrganizationData);
    state.userOrganizationsData = [...sortedUserOrganizationData];
  },
  [SET_CURRENT_ORGANIZATION_ADMINS](state, { currentOrganizationAdmins }) {
    state.currentOrganizationAdmins = currentOrganizationAdmins;
  },
  [SET_CURRENT_ORGANIZATION_ID](state, organizationId) {
    state.currentOrganizationId = organizationId;
  },
  [WITHDRAW_ORGANIZATION_ADMINS](state, withdrawOrganizationAdmins) {
    state.withdrawOrganizationAdmins = [...withdrawOrganizationAdmins];
  },
  [RESET_ORGANIZATIONS](state) {
    Object.assign(state, setInitialState());
  },
  [SET_OVERVIEW_DATA](state, overviewData) {
    state.overviewData = overviewData;
  },
  [RESET_OVERVIEW_DATA](state, overviewData) {
    state.overviewData = {};
  },
  [SET_PREV_OVERVIEW_DATA](state, prevOverviewData) {
    const numberOfLessons = sum(prevOverviewData.numberOfLessons);
		const numberOfMinutes = sum(prevOverviewData.numberOfMinutes);
		let previewData = {
			"lessons_count": numberOfLessons,
			"hours": numberOfMinutes / 60,
		};
    state.prevOverviewData = { ...previewData };
  },
  [SET_PENDING_INVITE_USERS](state, pendingInviteUsers) {
    state.pendingInviteUsers = pendingInviteUsers;
  },
  [SOFT_RESET_ORGANIZATIONS](state) {
    const initialState = omit(["types", "status", "userOrganizations", "userOrganizationsData"], setInitialState());

    Object.assign(state, initialState);
  },
  ...chartFragmentMutations,
  [LOADING_INDIVIDUAL_LEADERBOARD](state, loading) {
    state.loadingIndividualLeaderboard = loading;
  },
  [LOADING_TEAM_LEADERBOARD](state, loading) {
    state.loadingTeamLeaderboard = loading;
  },
  [LOADING_BRANCH_LEADERBOARD](state, loading) {
    state.loadingBranchLeaderboard = loading;
  },
  [LOADING_AVERAGE_SCORE_SKILLS](state, loading) {
    state.loadingAverageScoreSkills = loading;
  },
  [SET_GENERAL_OVERVIEW](state, generalOverviewData) {
    state.generalOverviewData = { ...generalOverviewData };
  },
  [SET_SUM_ACTIVE_STUDENTS](state, sumActiveStudents) {
		state.sumActiveStudents = sumActiveStudents;
	},
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
